/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type Ref,
	type RefAttributes,
	type RefObject,
	useCallback,
} from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { useRouter, useRouterActions } from 'react-resource-router-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditorSearchIcon from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/compiled';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const cssOverrideWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles
	'&& [data-ds--text-field--container]': {
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
	},
});

const styles = cssMap({
	wrapper: {
		width: '400px',
	},
	searchIcon: {
		minWidth: 'fit-content',
		minHeight: 'fit-content',
		marginLeft: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	clearIcon: {
		marginRight: token('space.100'),
	},
});

export type SearchTextFieldProps = {
	isReadOnly: boolean;
	keyboardRef: RefObject<HTMLInputElement>;
	onInput(value: string): void;
	placeholderText?: string;
	queryWord: string;
	searchBasePath?: string;
	showDropdown: boolean;
};

export const SearchTextField: ForwardRefExoticComponent<
	SearchTextFieldProps & RefAttributes<HTMLDivElement>
> = forwardRef((props: SearchTextFieldProps, ref: Ref<HTMLDivElement>) => {
	const { isReadOnly, onInput, placeholderText, queryWord, searchBasePath } = props;

	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [routerState] = useRouter();

	const handleChange: TextFieldProps['onChange'] = (e) => {
		const value = e.currentTarget.value;
		onInput(value);
	};

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();

				const currentPath = searchBasePath || routerState.location.pathname;
				const updatedPathIfHasMissingTrailingSlash = currentPath.endsWith('/')
					? currentPath
					: `${currentPath}/`;
				push(
					`${updatedPathIfHasMissingTrailingSlash}articles/search?q=${encodeURIComponent(queryWord)}`,
				);
			}
		},
		[searchBasePath, routerState?.location?.pathname, queryWord, push],
	);

	const handleClear = () => {
		onInput('');
	};

	return (
		<Box ref={ref} xcss={styles.wrapper}>
			<div css={cssOverrideWrapperStyles}>
				<Textfield
					ref={props.keyboardRef}
					id="article-search-query-input"
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					isReadOnly={isReadOnly}
					placeholder={placeholderText ?? formatMessage(messages.defaultPlaceholder)}
					value={queryWord}
					elemBeforeInput={
						<Box xcss={styles.searchIcon}>
							<EditorSearchIcon color="currentColor" spacing="spacious" label="" />
						</Box>
					}
					elemAfterInput={
						queryWord && (
							<Box xcss={styles.clearIcon}>
								<IconButton
									icon={(iconProps) => <CrossIcon {...iconProps} color="currentColor" label="" />}
									spacing="compact"
									onClick={handleClear}
									label=""
									appearance="subtle"
								/>
							</Box>
						)
					}
				/>
			</div>
		</Box>
	);
});
